import type { AccessibleFolderMeta } from '../../organize';
import type { FolderMutationOutboxService } from './folder-mutation-outbox';

export type NavigationPanelFolderVisibility = 'public' | 'private';

type FolderTrashLocalFirstDeps = {
  accessibleDocCache: {
    captureTrashSnapshot(docIds: string[]): unknown;
    restoreTrashSnapshot(snapshot: unknown): void;
    setDocsTrashState(docIds: string[], trashed: boolean): void;
  };
  accessibleFolderCache: {
    captureOptimisticSnapshot(): unknown;
    clearOptimisticTrashedFolder(
      folderId: string,
      outcome?: 'confirmed' | 'rejected'
    ): void;
    fetchAccessibleFolders(): Promise<unknown>;
    flushPendingStateWrites(): Promise<void>;
    getCachedFolderTreeDocIds(folderId: string): string[];
    optimisticRemoveFolder(folderId: string): void;
    optimisticTrashFolder(
      folderId: string,
      yjsFallbackFolders?: AccessibleFolderMeta[]
    ): unknown;
    restoreOptimisticSnapshot(snapshot: unknown): void;
    restoreOptimisticTrashedFolder(folderId: string): void;
  };
  folderMutationOutbox: Pick<FolderMutationOutboxService, 'enqueueTrashFolder'>;
  folderId: string;
  folderStore: {
    collectDescendantDocIds(folderId: string): string[];
    getYjsFolderSubtreeSnapshot(
      folderId: string,
      options?: {
        ownerUserId?: string | null;
        visibility?: NavigationPanelFolderVisibility;
      }
    ): AccessibleFolderMeta[];
    restoreFolderSilent(folderId: string): void;
    trashFolderSilent(folderId: string): void;
  };
  canTrashFolder: boolean;
  canUseYjsFolderRoute: boolean;
  notifyMoveFailed(): void;
  notifyRestoreFailed(): void;
  onTrashConfirmed?: () => void;
  showFolderTrashToast(options: { onUndo: () => Promise<void> }): void;
  visibility?: NavigationPanelFolderVisibility;
  logError?: (message: string, error: unknown) => void;
};

export function moveFolderToTrashLocalFirst({
  accessibleDocCache,
  folderMutationOutbox,
  accessibleFolderCache,
  folderId,
  folderStore,
  canTrashFolder,
  canUseYjsFolderRoute,
  notifyMoveFailed,
  notifyRestoreFailed,
  onTrashConfirmed,
  showFolderTrashToast,
  visibility = 'public',
  logError = console.error,
}: FolderTrashLocalFirstDeps): Promise<void> {
  if (!canTrashFolder) {
    return Promise.resolve();
  }

  if (!canUseYjsFolderRoute) {
    return folderMutationOutbox
      .enqueueTrashFolder(folderId)
      .then(() => undefined)
      .catch(error => {
        logError('Failed to queue folder trash:', error);
        notifyMoveFailed();
      });
  }

  const folderSnapshot = accessibleFolderCache.captureOptimisticSnapshot();
  const docIds = canUseYjsFolderRoute
    ? folderStore.collectDescendantDocIds(folderId)
    : accessibleFolderCache.getCachedFolderTreeDocIds(folderId);
  const docTrashSnapshot = accessibleDocCache.captureTrashSnapshot(docIds);
  const rollbackLocalTrash = () => {
    accessibleFolderCache.clearOptimisticTrashedFolder(folderId, 'rejected');
    accessibleFolderCache.restoreOptimisticSnapshot(folderSnapshot);
    if (canUseYjsFolderRoute) {
      folderStore.restoreFolderSilent(folderId);
    }
    accessibleDocCache.restoreTrashSnapshot(docTrashSnapshot);
  };

  const run = async () => {
    try {
      accessibleDocCache.setDocsTrashState(docIds, true);
      accessibleFolderCache.optimisticTrashFolder(
        folderId,
        folderStore.getYjsFolderSubtreeSnapshot(folderId, { visibility })
      );
      await accessibleFolderCache.flushPendingStateWrites();
      folderStore.trashFolderSilent(folderId);
      onTrashConfirmed?.();
      showFolderTrashToast({
        onUndo: async () => {
          try {
            accessibleFolderCache.restoreOptimisticTrashedFolder(folderId);
            await accessibleFolderCache.flushPendingStateWrites();
            folderStore.restoreFolderSilent(folderId);
            accessibleDocCache.setDocsTrashState(docIds, false);
          } catch (error) {
            logError('Failed to restore folder from trash:', error);
            notifyRestoreFailed();
          }
        },
      });
    } catch (error) {
      logError('Failed to move folder to trash locally:', error);
      rollbackLocalTrash();
      await accessibleFolderCache.flushPendingStateWrites();
      notifyMoveFailed();
    }
  };

  return run();
}
